/**
 * A AGENDA DO GOOGLE VISTA DO ITEM — quatro estados, não dois.
 *
 * O backend decide quem entra com `naAgenda = agendaManual ?? temPropostaEnviada`.
 * O item carrega três campos (vêm em `GET /procurement/items` e no `p.item` de
 * `GET /proposals`):
 *  - `googleEventId`: há evento publicado no Google;
 *  - `agendaManual`: a decisão HUMANA, tri-estado. `null` deixa o portão
 *    (proposta enviada) decidir; `true` põe mesmo sem proposta; `false` tira
 *    mesmo com proposta, e a varredura não recria;
 *  - `googleEventCarimbo`: o que foi mandado (não usado aqui).
 *
 * ⚠️ NÃO COLAPSE EM "NA AGENDA / FORA". "Marcado, sem evento" é o caso que
 * mais engana: a marca vale, mas o evento não nasceu (modo seco, dispensa sem
 * fim da disputa publicado, item sem data). Sem um selo próprio, o clique em
 * "Pôr na agenda" parece não ter funcionado.
 */

export type EstadoDaAgenda = 'na_agenda' | 'marcado_sem_evento' | 'fora' | null;

export function estadoDaAgenda(item: any): EstadoDaAgenda {
  if (!item) return null;
  if (item.googleEventId) return 'na_agenda';
  if (item.agendaManual === true) return 'marcado_sem_evento';
  if (item.agendaManual === false) return 'fora';
  return null;
}

export const SELO_DA_AGENDA: Record<Exclude<EstadoDaAgenda, null>, { rotulo: string; icone: string; cor: string; dica: string }> = {
  na_agenda: {
    rotulo: 'Na agenda',
    icone: '📅',
    cor: 'bg-emerald-500/15 text-emerald-300 border-emerald-600/40',
    dica: 'Há evento publicado na agenda do Google para esta sessão.',
  },
  marcado_sem_evento: {
    rotulo: 'Marcado, sem evento',
    icone: '📅',
    cor: 'bg-amber-500/15 text-amber-300 border-amber-600/40',
    dica: 'Marcado à mão para entrar na agenda, mas o evento ainda não foi criado.',
  },
  fora: {
    rotulo: 'Fora da agenda',
    icone: '⊘',
    cor: 'bg-slate-600/30 text-slate-400 border-slate-600/50',
    dica: 'Tirado da agenda à mão. Fica fora mesmo com proposta enviada; a varredura não recria o evento.',
  },
};

/** Resposta de `POST /procurement/items/:id/agenda`. */
export interface AgendaMarcarResposta {
  itemId: number;
  agendaManual: true;
  modo: 'seco' | 'ativo';
  publicado: boolean;
  googleEventId: string | null;
  motivo: string | null;
}

/** Resposta de `DELETE /procurement/items/:id/agenda`. */
export interface AgendaRemoverResposta {
  itemId: number;
  removido: boolean;
  googleEventIdAnterior: string | null;
  agendaManual: false;
  detalhe: string;
}

/**
 * Resposta de `GET /procurement/agenda/diagnostico`.
 * ⛔ Não há `gmail`: saiu do contrato em 01/10/2026 (PR back #278), por decisão
 * do Heitor. Não reintroduza.
 */
export interface AgendaDiagnostico {
  armada: boolean;
  motivo: string | null;
  modo: 'seco' | 'ativo';
  contaDeServico?: string | null;
  impersonando?: string | null;
  calendarId?: string | null;
  escopos?: string[];
  avisos?: string[];
  portao?: { regra: string; candidatosNaJanela: number; comPropostaEnviada: number };
  plano?: {
    total: number;
    porAcao: Record<string, number>;
    primeiros: Array<{ itemId: number; acao: string; titulo: string | null; motivo: string | null }>;
  };
}

/**
 * O que dizer ao usuário depois de "Pôr na agenda". Os dois casos de
 * `publicado: false` NÃO são falha: a marca ficou gravada.
 */
export function mensagemDoMarcar(r: AgendaMarcarResposta): string {
  if (r.publicado) return 'Posto na agenda do Google.';
  return `Marcado para a agenda. O evento ainda não foi criado${r.motivo ? `: ${r.motivo}` : '.'}`;
}

/** O que dizer depois de "Tirar da agenda". `removido: false` é o caso idempotente. */
export function mensagemDoRemover(r: AgendaRemoverResposta): string {
  const base = r.removido ? 'Evento cancelado na agenda do Google.' : 'Fora da agenda.';
  return r.detalhe ? `${base} ${r.detalhe}` : base;
}
